import React, { useCallback, useEffect, useState } from "react";
import { ModalProps } from "..";
import { Button, Input, Modal } from "../../components";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { showError } from "../../utils/error";
import toast from "react-hot-toast";
import { CategoryType } from "../../types";
import { createCategory_V2, updateCategory_V2 } from "../../Services_V2/Category.service";

interface Props extends ModalProps {
    refetch?: () => void;
    editableCategory?: CategoryType | null;
}

const schema = yup.object({
    name: yup.string().required("Please enter category name"),
});

interface FormType {
    name: string;
}

const AddEditAdminCategory: React.FC<Props> = ({
    open,
    onClose,
    editableCategory,
    refetch,
}) => {
    const [loading, setLoading] = useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm<FormType>({
        resolver: yupResolver(schema),
    });

    useEffect(() => {
        if (!open) {
            reset();
        }
    }, [open]);

    useEffect(() => {
        if (editableCategory) {
            setValue("name", editableCategory.name);
        }
    }, [editableCategory]);

    const onSubmit = useCallback(
        async (values: FormType) => {
            setLoading(true);
            if (editableCategory) {
                return updateCategory(values);
            }
            createCategory(values);
        },
        [onClose, refetch, editableCategory]
    );

    const createCategory = useCallback(async (values: FormType) => {
        try {
            const res = await createCategory_V2(values);
            toast.success(res?.message);
            refetch?.();
            onClose();
            reset();
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }, []);

    const updateCategory = useCallback(
        async (values: FormType) => {
            try {
                const res = await updateCategory_V2(editableCategory?._id as string, values);
                toast.success(res?.message);
                refetch?.();
                onClose();
                reset();
            } catch (error) {
                showError(error);
            } finally {
                setLoading(false);
            }
        },
        [editableCategory]
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            showClose={true}
            title={editableCategory ? "Update category" : "Add category"}
            loading={loading}
        >
            <form style={{ marginTop: "20px" }} onSubmit={handleSubmit(onSubmit)}>
                <Input
                    fullWidth
                    label="Category name"
                    placeholder="Enter here"
                    {...register("name")}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                />
                <Button fullWidth type="submit" sx={{ mt: 4 }} loading={loading}>
                    Save
                </Button>
            </form>
        </Modal>
    );
};

export default AddEditAdminCategory;
